/**
 * Global Color Palette
 *
 * This file contains all color definitions used throughout the application.
 * Colors are organized by semantic purpose and include both light and dark theme variants.
 */

// strikeUp Brand Palette (Brand Style Guide v1.0)
export const brand = {
  darkNavy: "#1D1D1F",
  electricBlue: "#3B82F6",
  paragraph: "#565656",
  lightParagraph: "#BEBEBE",
  warmWhite: "#F7F6F2",
  offWhite: "#F5F5F5",
  pureWhite: "#FFFFFF",
  pastelBlue: "#DDECF0",
  pastelYellow: "#F5EFDA",
  pastelGreen: "#E8E8DF",
  pastelPurple: "#EBEEFA",
};

// Gray Scale (Light Theme)
export const gray = {
  50: "#f9fafb",
  100: "#f3f4f6",
  200: "#e5e7eb",
  300: "#d1d5db",
  400: "#9ca3af",
  500: "#6b7280",
  600: "#4b5563",
  700: "#374151",
  800: "#1f2937",
  900: "#111827",
};

// Dark Mode Backgrounds
export const darkModeBackgrounds = {
  50: "#141417", // Black - 10
  100: "#28282B", // Neutral - 09
  200: "#3B3B3C", // Neutral - 08
  300: "#515153", // Neutral - 07
  400: "#646469", // Neutral - 06
  500: "#88898E", // Neutral - 05
  600: "#CACCD2", // Neutral - 04
  700: "#E3E3E7", // Neutral - 03
  800: "#F6F6FC", // Neutral - 02
  900: "#FFFFFF", // White - 01
};

// Light Mode Backgrounds
export const lightModeBackgrounds = {
  50: "#FFFFFF", // White - 01
  100: "#F6F6FC", // Neutral - 02
  200: "#E3E3E7", // Neutral - 03
  300: "#CACCD2", // Neutral - 04
  400: "#88898E", // Neutral - 05
  500: "#646469", // Neutral - 06
  600: "#515153", // Neutral - 07
  700: "#3B3B3C", // Neutral - 08
  800: "#28282B", // Neutral - 09
  900: "#141417", // Black - 10
};

// Semantic Colors - Success
export const green = {
  50: "#f0fdf4",
  100: "#dcfce7",
  200: "#bbf7d0",
  300: "#86efac",
  400: "#4ade80",
  500: "#22c55e",
  600: "#16a34a",
  700: "#15803d",
  800: "#166534",
  900: "#14532d",
};

// Semantic Colors - Error/Danger
export const red = {
  50: "#fef2f2",
  100: "#fee2e2",
  200: "#fecaca",
  300: "#fca5a5",
  400: "#f87171",
  500: "#ef4444",
  600: "#dc2626",
  700: "#b91c1c",
  800: "#991b1b",
  900: "#7f1d1d",
};

// Semantic Colors - Warning
export const yellow = {
  50: "#fefce8",
  100: "#fef9c3",
  200: "#fef08a",
  300: "#fde047",
  400: "#facc15",
  500: "#eab308",
  600: "#ca8a04",
  700: "#a16207",
  800: "#854d0e",
  900: "#713f12",
};

// Semantic Colors - Info
export const blue = {
  50: "#eff6ff",
  100: "#dbeafe",
  200: "#bfdbfe",
  300: "#93c5fd",
  400: "#60a5fa",
  500: "#3b82f6",
  600: "#2563eb",
  700: "#1d4ed8",
  800: "#1e40af",
  900: "#1e3a8a",
};

/**
 * Theme-specific color mappings
 *
 * These objects provide semantic color tokens that automatically adapt
 * based on the current theme (light/dark).
 */

export const lightTheme = {
  // Backgrounds
  bg: {
    primary: lightModeBackgrounds[50],
    secondary: brand.offWhite,
    tertiary: brand.warmWhite,
    hover: lightModeBackgrounds[200],
    active: lightModeBackgrounds[300],
    disabled: lightModeBackgrounds[200],
  },

  // Text colors
  text: {
    primary: brand.darkNavy,
    secondary: brand.paragraph,
    tertiary: brand.lightParagraph,
    disabled: lightModeBackgrounds[500],
    inverse: lightModeBackgrounds[50],
    link: brand.electricBlue,
    linkHover: blue[600],
  },

  // Border colors
  border: {
    primary: lightModeBackgrounds[200],
    default: lightModeBackgrounds[200],
    strong: lightModeBackgrounds[400],
    subtle: lightModeBackgrounds[200],
    focus: lightModeBackgrounds[600],
    error: red[200],
    success: green[200],
    warning: yellow[200],
    info: blue[200],
  },

  // Component specific
  navbar: {
    bg: lightModeBackgrounds[50],
    borderColor: lightModeBackgrounds[200],
    searchBg: lightModeBackgrounds[100],
  },

  sidebar: {
    bg: lightModeBackgrounds[50],
    divider: lightModeBackgrounds[200],
    linkColor: lightModeBackgrounds[700],
    linkActiveColor: lightModeBackgrounds[900],
    linkActiveBg: lightModeBackgrounds[800],
    hoverBg: lightModeBackgrounds[100],
  },

  // Semantic component colors
  codeBlock: {
    bg: lightModeBackgrounds[100],
    text: lightModeBackgrounds[900],
    border: lightModeBackgrounds[200],
  },

  modal: {
    overlay: "rgba(0, 0, 0, 0.5)",
    bg: lightModeBackgrounds[50],
    headerText: lightModeBackgrounds[900],
    closeIconColor: lightModeBackgrounds[600],
    closeIconHover: lightModeBackgrounds[800],
  },

  alert: {
    error: {
      bg: red[100],
      text: red[700],
      border: red[200],
    },
    success: {
      bg: green[100],
      text: green[700],
      border: green[200],
    },
    warning: {
      bg: yellow[100],
      text: yellow[700],
      border: yellow[200],
    },
    info: {
      bg: blue[100],
      text: blue[700],
      border: blue[200],
    },
  },

  progress: {
    bg: lightModeBackgrounds[300],
    fill: blue[500],
    fillComplete: green[500],
    text: blue[600],
    panelBg: blue[50],
    panelBorder: blue[200],
    cardBg: lightModeBackgrounds[50],
    cardBorder: blue[200],
  },

  input: {
    bg: "transparent",
    border: lightModeBackgrounds[200],
    borderFocus: lightModeBackgrounds[600],
    text: lightModeBackgrounds[900],
    placeholder: lightModeBackgrounds[500],
  },

  textarea: {
    bg: lightModeBackgrounds[100],
    border: lightModeBackgrounds[200],
    borderFocus: lightModeBackgrounds[600],
    text: lightModeBackgrounds[900],
    placeholder: lightModeBackgrounds[500],
  },

  card: {
    bg: lightModeBackgrounds[50],
    border: lightModeBackgrounds[200],
  },

  table: {
    headerBg: lightModeBackgrounds[100],
    headerText: lightModeBackgrounds[800],
    rowBg: lightModeBackgrounds[50],
    rowHover: lightModeBackgrounds[200],
    border: lightModeBackgrounds[200],
  },

  button: {
    primary: brand.electricBlue,
    primaryHover: blue[600],
    primaryText: lightModeBackgrounds[50],
    secondary: {
      bg: lightModeBackgrounds[200],
      hover: lightModeBackgrounds[300],
    },
  },

  tooltip: {
    bg: lightModeBackgrounds[900], // near-black for crisp legibility on light surface
    fg: lightModeBackgrounds[50], // off-white text
    border: "transparent",
  },

  skeleton: {
    bg: lightModeBackgrounds[100],
    highlight: lightModeBackgrounds[200],
  },
};

export const darkTheme = {
  // Backgrounds
  bg: {
    primary: darkModeBackgrounds[50], // Neutral-02 - Main background
    secondary: darkModeBackgrounds[50], // Black-01 - Darker sections
    tertiary: darkModeBackgrounds[200], // Neutral-03 - Cards/elevated surfaces
    hover: darkModeBackgrounds[300], // Neutral-04 - Hover states
    active: darkModeBackgrounds[400], // Neutral-05 - Active states
    disabled: darkModeBackgrounds[200], // Neutral-03 - Disabled elements
  },

  // Text colors
  text: {
    primary: darkModeBackgrounds[900], // White-10 - Primary text
    secondary: darkModeBackgrounds[600], // Neutral-07 - Secondary text
    tertiary: darkModeBackgrounds[500], // Neutral-06 - Tertiary/muted text
    disabled: darkModeBackgrounds[400], // Neutral-05 - Disabled text
    inverse: darkModeBackgrounds[50], // Black-01 - Inverse text
    link: brand.electricBlue,
    linkHover: blue[400],
  },

  // Border colors
  border: {
    primary: darkModeBackgrounds[300],
    default: darkModeBackgrounds[300], // Neutral-04 - Default borders
    strong: darkModeBackgrounds[400], // Neutral-05 - Strong borders
    subtle: darkModeBackgrounds[200], // Neutral-03 - Subtle borders
    focus: darkModeBackgrounds[500],
    error: red[700],
    success: green[700],
    warning: yellow[700],
    info: blue[700],
  },

  // Component specific
  navbar: {
    bg: darkModeBackgrounds[100], // Black-01 - Navbar background
    borderColor: darkModeBackgrounds[200], // Neutral-03 - Navbar border
    searchBg: darkModeBackgrounds[200], // Neutral-03 - Search background
  },

  sidebar: {
    bg: darkModeBackgrounds[50], // Black-01 - Sidebar background
    divider: darkModeBackgrounds[200], // Neutral-03 - Divider
    linkColor: darkModeBackgrounds[500], // Neutral-06 - Link color
    linkActiveColor: darkModeBackgrounds[900], // White-10 - Active link
    linkActiveBg: darkModeBackgrounds[700],
    hoverBg: darkModeBackgrounds[100], // Neutral-02 - Hover background
  },

  // Semantic component colors
  codeBlock: {
    bg: darkModeBackgrounds[200], // Neutral-03 - Code block background
    text: darkModeBackgrounds[900], // White-10 - Code text
    border: darkModeBackgrounds[300], // Neutral-04 - Code block border
  },

  modal: {
    overlay: "rgba(80, 80, 83, 0.8)", // Black-01 with opacity
    bg: darkModeBackgrounds[50], // Neutral-03 - Modal background
    headerText: darkModeBackgrounds[900], // White-10 - Header text
    closeIconColor: darkModeBackgrounds[600], // Neutral-07 - Close icon
    closeIconHover: darkModeBackgrounds[700], // Neutral-08 - Close icon hover
  },

  alert: {
    error: {
      bg: "rgba(220, 38, 38, 0.15)",
      text: red[300],
      border: red[800],
    },
    success: {
      bg: "rgba(34, 197, 94, 0.15)",
      text: green[300],
      border: green[800],
    },
    warning: {
      bg: "rgba(234, 179, 8, 0.15)",
      text: yellow[300],
      border: yellow[800],
    },
    info: {
      bg: "rgba(59, 130, 246, 0.15)",
      text: blue[300],
      border: blue[800],
    },
  },

  progress: {
    bg: darkModeBackgrounds[300], // Neutral-04 - Progress background
    fill: blue[500],
    fillComplete: green[500],
    text: blue[300],
    panelBg: darkModeBackgrounds[200], // Neutral-03 - Panel background
    panelBorder: darkModeBackgrounds[300], // Neutral-04 - Panel border
    cardBg: darkModeBackgrounds[200], // Neutral-03 - Card background
    cardBorder: darkModeBackgrounds[300], // Neutral-04 - Card border
  },

  input: {
    bg: "transparent",
    border: darkModeBackgrounds[300], // Neutral-04 - Input border
    borderFocus: darkModeBackgrounds[500],
    text: darkModeBackgrounds[900], // White-10 - Input text
    placeholder: darkModeBackgrounds[500], // Neutral-06 - Placeholder
  },

  textarea: {
    bg: darkModeBackgrounds[200], // Neutral-03 - Textarea background
    border: darkModeBackgrounds[300], // Neutral-04 - Textarea border
    borderFocus: darkModeBackgrounds[500],
    text: darkModeBackgrounds[900], // White-10 - Textarea text
    placeholder: darkModeBackgrounds[500], // Neutral-06 - Placeholder
  },

  card: {
    bg: darkModeBackgrounds[200], // Neutral-02 - Card background (same as navbar)
    border: darkModeBackgrounds[300], // Neutral-04 - Card border
  },

  table: {
    headerBg: darkModeBackgrounds[100], // Neutral-02 - Table header (same as navbar)
    headerText: darkModeBackgrounds[500], // Neutral-06 - Header text (muted)
    rowBg: darkModeBackgrounds[50], // Black-01 - Row background (same as main content)
    rowHover: darkModeBackgrounds[100], // Neutral-03 - Row hover
    border: darkModeBackgrounds[200], // Neutral-03 - Table borders (subtle)
  },

  button: {
    primary: brand.electricBlue,
    primaryHover: blue[600],
    primaryText: darkModeBackgrounds[900],
    secondary: {
      bg: darkModeBackgrounds[300],
      hover: darkModeBackgrounds[400],
    },
  },

  tooltip: {
    bg: darkModeBackgrounds[800], // near-white on dark surface — flips for legibility
    fg: darkModeBackgrounds[50], // very dark text
    border: "transparent",
  },

  skeleton: {
    bg: darkModeBackgrounds[100],
    highlight: darkModeBackgrounds[200],
  },
};

/**
 * Helper function to get theme-specific colors
 *
 * Usage example:
 * const bgColor = getThemeColor('bg.primary');
 * Returns: { base: lightTheme.bg.primary, _dark: darkTheme.bg.primary }
 */
export const getThemeColor = (path: string) => {
  const keys = path.split(".");
  let lightValue: any = lightTheme;
  let darkValue: any = darkTheme;

  for (const key of keys) {
    lightValue = lightValue?.[key];
    darkValue = darkValue?.[key];
  }

  return {
    base: lightValue,
    _dark: darkValue,
  };
};

/**
 * Utility to create Chakra UI color palette format
 */
export const createChakraColor = (colorName: string, value: string) => ({
  [colorName]: { value },
});

// Export all colors as a single object for convenience
export const colors = {
  brand,
  gray,
  lightModeBackgrounds,
  darkModeBackgrounds,
  green,
  red,
  yellow,
  blue,
};

export default colors;
